/*
 * variant-color-match.js
 *
 * Makes the "Better as a Set" (complementary) recommendation cards follow the
 * colour/pattern the shopper selects on the main product.
 *
 * Each recommendation card's slide wrapper carries `data-color-match`, a JSON map
 * of that product's colours -> { url, img, price, compare, available } (see
 * snippets/color-match-data.liquid). When the shopper switches colour on the main
 * product, we look up the selected colour value in each card's map and rewrite the
 * card's links, image and price to the matching colour. Cards whose product does
 * not come in the selected colour are left on (or restored to) their original state.
 *
 * The recommendation block is loaded asynchronously and is NOT re-rendered when the
 * variant changes, so we (a) listen for colour changes via delegation on `document`
 * (which survives the variant picker being swapped out by the section-rendering
 * update) and (b) use a MutationObserver to catch the cards once they load.
 */
(function () {
  if (window.__variantColorMatchInit) return;
  window.__variantColorMatchInit = true;

  var SLIDE_SELECTOR = '[data-color-match]';
  var IMG_SELECTOR = 'img.shape__target-image';
  var LINK_SELECTOR =
    'a.vertical-product-card__link, a.vertical-product-card__title,' +
    'a.horizontal-product-card__link, a.horizontal-product-card__title';
  var PRICE_SELECTOR =
    '.vertical-product-card__price-item, .horizontal-product-card__price-item';
  var OLD_PRICE_SELECTOR =
    '.vertical-product-card__old-price-item, .horizontal-product-card__old-price-item';
  var CARD_SELECTOR = 'vertical-product-card, horizontal-product-card';
  var IMG_WIDTHS = [300, 400, 600, 800];

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  function buildSized(url, width) {
    if (!url) return url;
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function buildSrcset(url) {
    return IMG_WIDTHS.map(function (w) {
      return buildSized(url, w) + ' ' + w + 'w';
    }).join(', ');
  }

  // Read the colour value(s) currently selected in the section's variant picker.
  function getSelectedValues(section) {
    if (!section) return [];
    var picker = section.querySelector('variant-picker');
    if (!picker) return [];
    return toArray(picker.querySelectorAll('input[type="radio"]:checked'))
      .map(function (input) {
        return (input.value || '').toLowerCase().trim();
      })
      .filter(Boolean);
  }

  function cacheOriginal(slide) {
    if (slide.__cmOriginal) return;
    var img = slide.querySelector(IMG_SELECTOR);
    var links = toArray(slide.querySelectorAll(LINK_SELECTOR));
    var price = slide.querySelector(PRICE_SELECTOR);
    var oldPrice = slide.querySelector(OLD_PRICE_SELECTOR);
    slide.__cmOriginal = {
      href: links.length ? links[0].getAttribute('href') : null,
      imgSrc: img ? img.getAttribute('src') : null,
      imgSrcset: img ? img.getAttribute('srcset') : null,
      price: price ? price.innerHTML : null,
      oldPrice: oldPrice ? oldPrice.innerHTML : null
    };
  }

  function applyToSlide(slide, selectedValues) {
    var raw = slide.getAttribute('data-color-match');
    if (!raw) return;

    var map;
    try {
      map = JSON.parse(raw);
    } catch (e) {
      return;
    }

    cacheOriginal(slide);

    var match = null;
    for (var i = 0; i < selectedValues.length; i++) {
      if (map[selectedValues[i]]) {
        match = map[selectedValues[i]];
        break;
      }
    }

    var img = slide.querySelector(IMG_SELECTOR);
    var links = toArray(slide.querySelectorAll(LINK_SELECTOR));
    var price = slide.querySelector(PRICE_SELECTOR);
    var oldPrice = slide.querySelector(OLD_PRICE_SELECTOR);
    var card = slide.querySelector(CARD_SELECTOR);
    var original = slide.__cmOriginal;

    if (match) {
      links.forEach(function (a) {
        a.setAttribute('href', match.url);
      });
      if (card) card.setAttribute('data-url', match.url);

      if (img && match.img) {
        img.setAttribute('src', buildSized(match.img, 600));
        img.setAttribute('srcset', buildSrcset(match.img));
      }
      if (price && match.price) {
        price.textContent = match.price;
      }
      if (oldPrice) {
        if (match.compare) {
          oldPrice.textContent = match.compare;
          oldPrice.style.display = '';
        } else {
          oldPrice.style.display = 'none';
        }
      }
    } else if (original) {
      // Selected colour isn't offered by this product — restore the default card.
      if (original.href != null) {
        links.forEach(function (a) {
          a.setAttribute('href', original.href);
        });
        if (card) card.setAttribute('data-url', original.href.split('?')[0]);
      }
      if (img) {
        if (original.imgSrc != null) img.setAttribute('src', original.imgSrc);
        if (original.imgSrcset != null) img.setAttribute('srcset', original.imgSrcset);
        else img.removeAttribute('srcset');
      }
      if (price && original.price != null) price.innerHTML = original.price;
      if (oldPrice && original.oldPrice != null) {
        oldPrice.innerHTML = original.oldPrice;
        oldPrice.style.display = '';
      }
    }
  }

  function processSection(section) {
    var slides = toArray(section.querySelectorAll(SLIDE_SELECTOR));
    if (!slides.length) return;
    var selectedValues = getSelectedValues(section);
    slides.forEach(function (slide) {
      applyToSlide(slide, selectedValues);
    });
  }

  function processAll() {
    toArray(document.querySelectorAll('.shopify-section')).forEach(function (section) {
      if (section.querySelector('variant-picker') && section.querySelector(SLIDE_SELECTOR)) {
        processSection(section);
      }
    });
  }

  // Colour change on the main product (delegated so it survives picker re-renders).
  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches || !target.matches('input[type="radio"]')) return;
    var picker = target.closest('variant-picker');
    if (!picker) return;
    var section = picker.closest('.shopify-section');
    if (section) processSection(section);
  });

  // Recommendation cards load asynchronously; re-run once they appear.
  var observer = new MutationObserver(function (mutations) {
    var needsRun = false;
    for (var i = 0; i < mutations.length && !needsRun; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (
          (node.matches && node.matches(SLIDE_SELECTOR)) ||
          (node.querySelector && node.querySelector(SLIDE_SELECTOR))
        ) {
          needsRun = true;
          break;
        }
      }
    }
    if (needsRun) processAll();
  });

  function start() {
    processAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
